import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Check, X } from 'lucide-react';
import { validatePassword } from '../../utils/formValidationUtils';

/**
 * PasswordField Component
 * Password input with show/hide toggle and strength indicator
 *
 * @param {string} label - Label text
 * @param {string} value - Current password value
 * @param {function} onChange - Change handler
 * @param {function} onBlur - Blur handler
 * @param {string} error - Error message
 * @param {boolean} showStrengthIndicator - Show password strength meter
 * @param {boolean} showRequirements - Show requirements checklist
 * @param {boolean} required - Mark field as required
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable input
 * @param {string} id - Input ID
 * @param {string} name - Input name
 * @param {string} autoComplete - Autocomplete attribute
 */
export default function PasswordField({
  label = 'Password',
  value,
  onChange,
  onBlur,
  error,
  showStrengthIndicator = true,
  showRequirements = false,
  required = false,
  placeholder = '••••••••',
  disabled = false,
  id,
  name = 'password',
  autoComplete = 'current-password'
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [validation, setValidation] = useState({
    strength: 'none',
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false
    }
  });

  const inputId = id || name;
  const errorId = `${inputId}-error`;

  // Validate password on value change
  useEffect(() => {
    if (value) {
      const result = validatePassword(value);
      setValidation(result);
    } else {
      setValidation({
        strength: 'none',
        requirements: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecial: false
        }
      });
    }
  }, [value]);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const toggleShowPassword = () => setShowPassword(!showPassword);

  // Strength indicator colors and labels
  const strengthConfig = {
    none: { color: 'bg-[var(--bg-tertiary)]', label: '', width: '0%' },
    weak: { color: 'bg-[var(--status-danger)]', label: 'Weak', width: '33%' },
    medium: { color: 'bg-[var(--status-warning)]', label: 'Medium', width: '66%' },
    strong: { color: 'bg-[var(--status-success)]', label: 'Strong', width: '100%' }
  };

  const currentStrength = strengthConfig[validation.strength];

  // Input state classes
  let inputStateClasses = 'border-[var(--border-subtle)] focus:ring-[var(--focus-ring)] focus:border-[var(--brand-terracotta)]';
  if (error) {
    inputStateClasses = 'border-[var(--status-danger)] focus:ring-[var(--status-danger)] focus:border-[var(--status-danger)]';
  } else if (validation.strength === 'strong') {
    inputStateClasses = 'border-[var(--status-success)] focus:ring-[var(--status-success)] focus:border-[var(--status-success)]';
  }

  const inputClasses = `
    w-full pl-12 pr-20 py-3 rounded-xl border-2
    text-[var(--text-primary)] placeholder-[var(--text-muted)]
    transition-all duration-200
    focus:outline-none focus:ring-2
    ${inputStateClasses}
    ${disabled ? 'bg-[var(--bg-tertiary)] cursor-not-allowed opacity-60' : 'bg-[var(--surface-card)]'}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-[var(--text-secondary)] mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Lock Icon */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
          <Lock size={20} />
        </div>

        {/* Password Input */}
        <input
          id={inputId}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : undefined}
          aria-required={required ? 'true' : 'false'}
        />

        {/* Show/Hide Toggle Button */}
        <button
          type="button"
          onClick={toggleShowPassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>

        {/* Success Indicator */}
        {validation.strength === 'strong' && !error && (
          <div className="absolute right-14 top-1/2 -translate-y-1/2 text-green-500">
            <CheckCircle size={20} />
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={errorId}
          role="alert"
          className="mt-1 text-xs text-red-600 flex items-center gap-1"
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Strength Indicator */}
      {showStrengthIndicator && value && !error && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[var(--text-secondary)]">Password strength</span>
            <span className={`text-xs font-semibold ${
              validation.strength === 'strong' ? 'text-[var(--status-success)]' :
              validation.strength === 'medium' ? 'text-[var(--status-warning)]' :
              validation.strength === 'weak' ? 'text-[var(--status-danger)]' :
              'text-[var(--text-muted)]'
            }`}>
              {currentStrength.label}
            </span>
          </div>
          <div className="h-2 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
            <div
              className={`h-full ${currentStrength.color} transition-all duration-300`}
              style={{ width: currentStrength.width }}
            />
          </div>
        </div>
      )}

      {/* Requirements Checklist */}
      {showRequirements && (isFocused || value) && !error && (
        <div className="mt-3 p-3 bg-[var(--bg-secondary)] rounded-lg space-y-1.5">
          <p className="text-xs font-semibold text-[var(--text-secondary)] mb-2">Password must contain:</p>

          <RequirementItem
            met={validation.requirements.minLength}
            text="At least 8 characters"
          />
          <RequirementItem
            met={validation.requirements.hasUppercase}
            text="One uppercase letter (A-Z)"
          />
          <RequirementItem
            met={validation.requirements.hasLowercase}
            text="One lowercase letter (a-z)"
          />
          <RequirementItem
            met={validation.requirements.hasNumber}
            text="One number (0-9)"
          />
          <RequirementItem
            met={validation.requirements.hasSpecial}
            text="One special character (!@#$%...)"
          />
        </div>
      )}
    </div>
  );
}

/**
 * RequirementItem Component
 * Individual requirement in the checklist
 */
function RequirementItem({ met, text }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {met ? (
        <Check size={14} className="text-green-500 flex-shrink-0" />
      ) : (
        <X size={14} className="text-[var(--text-muted)] flex-shrink-0" />
      )}
      <span className={met ? 'text-green-700' : 'text-[var(--text-muted)]'}>{text}</span>
    </div>
  );
}
